import React, { useState, useEffect } from 'react';
import {
  FileText, CreditCard, Award, Users, Download, Eye, Printer,
  Search, GraduationCap, Shield, ScrollText, Building2
} from 'lucide-react';
import api from '../../utils/api';
import { useTheme } from '../../context/ThemeContext';
import {
  generateReportCard,
  generateIDCard,
  generateTC,
  generateBonafide,
  generateCharacterCert
} from '../../utils/pdfGenerators';
import '../styles/generators.css';

const GeneratorsPage = () => {
  const { themeColors, schoolLogo } = useTheme();
  const [students, setStudents] = useState([]);
  const [classes, setClasses] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState('');
  const [selectedClass, setSelectedClass] = useState('');
  const [selectedSection, setSelectedSection] = useState('');
  const [academicYear, setAcademicYear] = useState(`${new Date().getFullYear()}-${new Date().getFullYear() + 1}`);
  const [purpose, setPurpose] = useState('');
  const [reason, setReason] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('report-card');
  const [message, setMessage] = useState({ type: '', text: '' });

  const schoolInfo = {
    name: 'Springfield Academy',
    address: '123 Education Lane, Knowledge City, Karnataka - 560001',
    phone: '+91 80 1234 5678',
    email: 'info@springfield.edu',
  };

  useEffect(() => {
    fetchStudents();
  }, []);

  const fetchStudents = async () => {
    try {
      const res = await api.get('/students?limit=500');
      const list = res.data.students || res.data || [];
      const studentsList = Array.isArray(list) ? list : [];
      setStudents(studentsList);
      const uniqueClasses = [...new Set(studentsList.map(s => s.class))].sort();
      setClasses(uniqueClasses);
    } catch (err) {
      console.error('Failed to load students', err);
      // Students will be loaded from API when backend is available
    }
  };

  const filteredStudents = students.filter(s => {
    const name = s.name || `${s.firstName || ''} ${s.lastName || ''}`;
    const matchSearch = !searchTerm ||
      name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      s.admissionNo?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchClass = !selectedClass || s.class === selectedClass;
    const matchSection = !selectedSection || s.section === selectedSection;
    return matchSearch && matchClass && matchSection;
  });

  const sections = selectedClass
    ? [...new Set(students.filter(s => s.class === selectedClass).map(s => s.section))].sort()
    : [];

  const selectedStudentObj = students.find(s => (s._id || s.id) === selectedStudent);

  const showMsg = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: '', text: '' }), 4000);
  };

  const previewPDF = (generatorFn, label, ...extraArgs) => {
    if (!selectedStudentObj) return showMsg('error', 'Please select a student');
    setLoading(true);
    try {
      const doc = generatorFn(selectedStudentObj, schoolInfo, ...extraArgs);
      const blob = doc.output('blob');
      window.open(URL.createObjectURL(blob), '_blank');
      showMsg('success', `${label} preview opened in new tab`);
    } catch (err) {
      console.error(err);
      showMsg('error', `Failed to preview ${label}`);
    }
    setLoading(false);
  };

  const downloadPDF = (generatorFn, filename, label, ...extraArgs) => {
    if (!selectedStudentObj) return showMsg('error', 'Please select a student');
    setLoading(true);
    try {
      const doc = generatorFn(selectedStudentObj, schoolInfo, ...extraArgs);
      doc.save(`${filename}_${selectedStudentObj.admissionNo}.pdf`);
      showMsg('success', `${label} downloaded for ${selectedStudentObj.firstName} ${selectedStudentObj.lastName}`);
    } catch (err) {
      console.error(err);
      showMsg('error', `Failed to generate ${label}`);
    }
    setLoading(false);
  };

  const tabs = [
    { id: 'report-card', label: 'Report Card', icon: FileText, color: '#d4a053' },
    { id: 'id-card', label: 'ID Card', icon: CreditCard, color: '#0ea5e9' },
    { id: 'tc', label: 'Transfer Certificate', icon: ScrollText, color: '#f59e0b' },
    { id: 'bonafide', label: 'Bonafide', icon: Award, color: '#10b981' },
    { id: 'character', label: 'Character Cert', icon: Shield, color: '#b8860b' },
    { id: 'udise', label: 'U-DISE Report', icon: Building2, color: '#4b2a6b' },
  ];

  return (
    <div className="generators-page">
      {message.text && (
        <div
          className={`gen-message ${message.type}`}
          style={{
            padding: '12px 16px',
            borderRadius: '8px',
            marginBottom: '16px',
            backgroundColor: message.type === 'success' ? '#dcfce7' : '#fee2e2',
            color: message.type === 'success' ? '#166534' : '#991b1b',
            border: `1px solid ${message.type === 'success' ? '#86efac' : '#fca5a5'}`,
            fontWeight: 500,
          }}
        >
          {message.text}
        </div>
      )}

      <div className="gen-tabs">
        {tabs.map(tab => (
          <button
            key={tab.id}
            className={`gen-tab ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
            style={activeTab === tab.id ? { borderColor: tab.color, color: tab.color } : {}}
          >
            <tab.icon size={18} />
            <span>{tab.label}</span>
          </button>
        ))}
      </div>

      <div className="gen-content">
        {/* Student Selector */}
        <div className="gen-card selector-card">
          <h3><Users size={18} /> Select Student</h3>
          <div className="gen-filters">
            <div className="gen-field">
              <label>Search</label>
              <div className="search-input">
                <Search size={16} />
                <input
                  type="text"
                  placeholder="Name or admission no..."
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                />
              </div>
            </div>
            <div className="gen-field">
              <label>Class</label>
              <select value={selectedClass} onChange={e => { setSelectedClass(e.target.value); setSelectedSection(''); }}>
                <option value="">All Classes</option>
                {classes.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div className="gen-field">
              <label>Section</label>
              <select value={selectedSection} onChange={e => setSelectedSection(e.target.value)}>
                <option value="">All Sections</option>
                {sections.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div className="gen-field">
              <label>Academic Year</label>
              <input type="text" value={academicYear} onChange={e => setAcademicYear(e.target.value)} />
            </div>
          </div>

          <div className="student-list">
            {filteredStudents.slice(0, 50).map(s => (
              <div
                key={s._id || s.id}
                className={`student-chip ${selectedStudent === (s._id || s.id) ? 'selected' : ''}`}
                onClick={() => setSelectedStudent(s._id || s.id)}
              >
                <div className="chip-avatar">{s.firstName?.[0]}{s.lastName?.[0]}</div>
                <div className="chip-info">
                  <span className="chip-name">{s.firstName} {s.lastName}</span>
                  <span className="chip-meta">{s.class}-{s.section} | {s.admissionNo}</span>
                </div>
              </div>
            ))}
            {filteredStudents.length === 0 && <p className="no-data">No students found</p>}
            {filteredStudents.length > 50 && <p className="no-data">Showing first 50 results. Refine your search.</p>}
          </div>
        </div>

        {/* Generator Panel */}
        <div className="gen-card generator-panel">
          {selectedStudentObj && (
            <div className="selected-student-banner">
              <div className="banner-avatar">{selectedStudentObj.firstName?.[0]}{selectedStudentObj.lastName?.[0]}</div>
              <div>
                <h4>{selectedStudentObj.firstName} {selectedStudentObj.lastName}</h4>
                <p>Class {selectedStudentObj.class}-{selectedStudentObj.section} | Adm: {selectedStudentObj.admissionNo} | Roll: {selectedStudentObj.rollNo || 'N/A'}</p>
              </div>
            </div>
          )}

          {activeTab === 'report-card' && (
            <div className="gen-section">
              <div className="gen-section-header" style={{ borderColor: '#d4a053' }}>
                <FileText size={22} color="#d4a053" />
                <div>
                  <h3>Report Card / Progress Report</h3>
                  <p>Generate a professional, school-branded PDF report card with all exam results, grades, and overall performance summary.</p>
                </div>
              </div>
              <div className="gen-actions">
                <button className="gen-btn primary" onClick={() => previewPDF(generateReportCard, 'Report Card', [], themeColors, schoolLogo)} disabled={loading || !selectedStudent}>
                  <Eye size={16} /> Preview Report Card
                </button>
                <button className="gen-btn secondary" onClick={() => downloadPDF(generateReportCard, 'ReportCard', 'Report Card', [], themeColors, schoolLogo)} disabled={loading || !selectedStudent}>
                  <Download size={16} /> Download PDF
                </button>
              </div>
              <div className="gen-preview-info">
                <h4>Report includes:</h4>
                <ul>
                  <li>School header with gold branding</li>
                  <li>Student personal details</li>
                  <li>All exam results with subject-wise marks</li>
                  <li>Percentage and grades per subject</li>
                  <li>Overall exam totals and percentages</li>
                  <li>Grading scale reference</li>
                  <li>Signature fields for Teacher, Principal, Parent</li>
                </ul>
              </div>
            </div>
          )}

          {activeTab === 'id-card' && (
            <div className="gen-section">
              <div className="gen-section-header" style={{ borderColor: '#0ea5e9' }}>
                <CreditCard size={22} color="#0ea5e9" />
                <div>
                  <h3>Student ID Card</h3>
                  <p>Generate credit-card-sized ID cards with student photo placeholder, details, and emergency contact info.</p>
                </div>
              </div>
              <div className="gen-actions">
                <button className="gen-btn primary" onClick={() => previewPDF(generateIDCard, 'ID Card', themeColors, schoolLogo)} disabled={loading || !selectedStudent}>
                  <Eye size={16} /> Preview ID Card
                </button>
                <button className="gen-btn secondary" onClick={() => downloadPDF(generateIDCard, 'IDCard', 'ID Card', themeColors, schoolLogo)} disabled={loading || !selectedStudent}>
                  <Download size={16} /> Download PDF
                </button>
              </div>
              <div className="gen-preview-info">
                <h4>ID Card includes:</h4>
                <ul>
                  <li>Front: School branding, photo placeholder, student details</li>
                  <li>Back: Important rules, parent contacts</li>
                  <li>Emergency contact information</li>
                  <li>Validity period &amp; blood group</li>
                </ul>
              </div>
            </div>
          )}

          {activeTab === 'tc' && (
            <div className="gen-section">
              <div className="gen-section-header" style={{ borderColor: '#f59e0b' }}>
                <ScrollText size={22} color="#f59e0b" />
                <div>
                  <h3>Transfer Certificate (TC)</h3>
                  <p>Generate an official Transfer Certificate for students leaving the institution.</p>
                </div>
              </div>
              <div className="gen-field" style={{ maxWidth: 400 }}>
                <label>Reason for Leaving</label>
                <select value={reason} onChange={e => setReason(e.target.value)}>
                  <option value="">Select reason...</option>
                  <option value="Parent Request">Parent Request</option>
                  <option value="Transfer to another school">Transfer to another school</option>
                  <option value="Relocation">Relocation</option>
                  <option value="Completed final year">Completed final year</option>
                  <option value="Other">Other</option>
                </select>
              </div>
              <div className="gen-actions">
                <button className="gen-btn primary" onClick={() => previewPDF(generateTC, 'Transfer Certificate', reason || 'Parent Request', themeColors, schoolLogo)} disabled={loading || !selectedStudent}>
                  <Eye size={16} /> Preview TC
                </button>
                <button className="gen-btn secondary" onClick={() => downloadPDF(generateTC, 'TC', 'Transfer Certificate', reason || 'Parent Request', themeColors, schoolLogo)} disabled={loading || !selectedStudent}>
                  <Download size={16} /> Download PDF
                </button>
              </div>
              <div className="gen-preview-info">
                <h4>TC includes:</h4>
                <ul>
                  <li>Official TC number with year</li>
                  <li>14 standard TC fields (name, DOB, class, conduct, etc.)</li>
                  <li>Signature fields for Teacher, Principal, and School Seal</li>
                </ul>
              </div>
            </div>
          )}

          {activeTab === 'bonafide' && (
            <div className="gen-section">
              <div className="gen-section-header" style={{ borderColor: '#10b981' }}>
                <Award size={22} color="#10b981" />
                <div>
                  <h3>Bonafide Certificate</h3>
                  <p>Certify that the student is a genuine member of the institution.</p>
                </div>
              </div>
              <div className="gen-field" style={{ maxWidth: 400 }}>
                <label>Purpose of Certificate</label>
                <input
                  type="text"
                  placeholder="e.g. Bank account opening, Passport application..."
                  value={purpose}
                  onChange={e => setPurpose(e.target.value)}
                />
              </div>
              <div className="gen-actions">
                <button className="gen-btn primary" onClick={() => previewPDF(generateBonafide, 'Bonafide Certificate', purpose, academicYear, themeColors, schoolLogo)} disabled={loading || !selectedStudent}>
                  <Eye size={16} /> Preview Bonafide
                </button>
                <button className="gen-btn secondary" onClick={() => downloadPDF(generateBonafide, 'Bonafide', 'Bonafide Certificate', purpose, academicYear, themeColors, schoolLogo)} disabled={loading || !selectedStudent}>
                  <Download size={16} /> Download PDF
                </button>
              </div>
              <div className="gen-preview-info">
                <h4>Certificate includes:</h4>
                <ul>
                  <li>Official reference number</li>
                  <li>"To Whom It May Concern" format</li>
                  <li>Student details with admission info</li>
                  <li>Date of birth verification</li>
                  <li>Purpose statement</li>
                  <li>Principal signature and school seal</li>
                </ul>
              </div>
            </div>
          )}

          {activeTab === 'character' && (
            <div className="gen-section">
              <div className="gen-section-header" style={{ borderColor: '#b8860b' }}>
                <Shield size={22} color="#b8860b" />
                <div>
                  <h3>Character Certificate</h3>
                  <p>Issue an official Character Certificate attesting to the student's conduct and behavior.</p>
                </div>
              </div>
              <div className="gen-actions">
                <button className="gen-btn primary" onClick={() => previewPDF(generateCharacterCert, 'Character Certificate', themeColors, schoolLogo)} disabled={loading || !selectedStudent}>
                  <Eye size={16} /> Preview Character Cert
                </button>
                <button className="gen-btn secondary" onClick={() => downloadPDF(generateCharacterCert, 'Character', 'Character Certificate', themeColors, schoolLogo)} disabled={loading || !selectedStudent}>
                  <Download size={16} /> Download PDF
                </button>
              </div>
              <div className="gen-preview-info">
                <h4>Certificate includes:</h4>
                <ul>
                  <li>Official reference number</li>
                  <li>"To Whom It May Concern" format</li>
                  <li>Period of attendance</li>
                  <li>Conduct and behavior attestation</li>
                  <li>Co-curricular participation note</li>
                  <li>Signatures: Class Teacher, Principal, School Seal</li>
                </ul>
              </div>
            </div>
          )}

          {activeTab === 'udise' && (
            <UdisePanel setMessage={setMessage} />
          )}
        </div>
      </div>
    </div>
  );
};

// ============================================================
// U-DISE tab — previews what the annual report will contain and
// lets the admin download CSV (for portal upload) or PDF (archive).
// ============================================================
const UdisePanel = ({ setMessage }) => {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get('/udise/preview');
        setPreview(data);
      } catch (err) {
        setMessage({ type: 'error', text: err?.response?.data?.message || 'Could not load U-DISE preview' });
      } finally {
        setLoading(false);
      }
    })();
  }, [setMessage]);

  const download = async (format) => {
    setDownloading(format);
    try {
      const res = await api.get('/udise/export', { params: { format }, responseType: 'blob' });
      const cd = res.headers['content-disposition'] || '';
      const match = /filename="?([^";]+)"?/.exec(cd);
      const name = match?.[1] || `udise.${format}`;
      const url = URL.createObjectURL(res.data);
      const link = document.createElement('a');
      link.href = url;
      link.download = name;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      setMessage({ type: 'success', text: `${format.toUpperCase()} downloaded — ${name}` });
    } catch (err) {
      setMessage({ type: 'error', text: 'Download failed' });
    } finally {
      setDownloading(null);
    }
  };

  if (loading) return <div style={{ padding: 20, color: '#6b7280' }}>Loading preview…</div>;
  if (!preview) return null;

  const { school, counts, teachers } = preview;
  const missing = [
    !school.udiseCode && 'U-DISE+ code (11 digits)',
    !school.schoolCategory && 'School category',
    !school.schoolManagement && 'Management type',
    !school.mediumOfInstruction && 'Medium of instruction',
    school.totalClassrooms == null && 'Total classrooms count',
  ].filter(Boolean);

  const tile = (label, value, hint) => (
    <div style={{ background: '#fff', border: '1px solid #ece3d2', borderRadius: 10, padding: 14 }}>
      <div style={{ fontSize: 11, color: '#8a7560', textTransform: 'uppercase', letterSpacing: 0.8, fontWeight: 600 }}>{label}</div>
      <div style={{ fontSize: 24, fontWeight: 700, color: '#1a1410', marginTop: 4, lineHeight: 1 }}>{value}</div>
      {hint && <div style={{ fontSize: 11.5, color: '#8a7560', marginTop: 4 }}>{hint}</div>}
    </div>
  );

  return (
    <div>
      <h2 style={{ fontSize: 20, fontWeight: 700, color: '#1a1410', marginBottom: 4 }}>U-DISE+ Annual Report</h2>
      <p style={{ color: '#46362a', fontSize: 13.5, margin: 0, marginBottom: 16 }}>
        Auto-generated from your student + teacher + school data. Download the CSV for direct upload to the U-DISE+ portal,
        or the PDF as an offline archive for your principal's file.
      </p>

      {missing.length > 0 && (
        <div style={{ padding: 12, borderRadius: 8, background: '#fef3c7', border: '1px solid #fde68a', color: '#854d0e', fontSize: 13, marginBottom: 16 }}>
          <strong>Heads up:</strong> {missing.length} field{missing.length === 1 ? ' is' : 's are'} not yet set on this school
          — the report will show "— (enter manually)" for those: <em>{missing.join(', ')}</em>.
          Fill them on the Settings page to make next year's export fully automated.
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 10, marginBottom: 16 }}>
        {tile('Students', counts.total, 'active enrolment')}
        {tile('Teachers', teachers.total, `${teachers.byGender.male || 0} M · ${teachers.byGender.female || 0} F`)}
        {tile('Boys', counts.byGender.male || 0)}
        {tile('Girls', counts.byGender.female || 0)}
        {tile('CWSN', counts.cwsn, 'special needs')}
        {tile('RTE quota', counts.rte, '25% admission pool')}
      </div>

      <div style={{ display: 'flex', gap: 10 }}>
        <button
          onClick={() => download('csv')}
          disabled={downloading !== null}
          style={{ padding: '10px 18px', background: '#4b2a6b', color: '#fff', border: 'none', borderRadius: 8, fontSize: 13.5, fontWeight: 600, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 8 }}
        >
          <Download size={14} /> {downloading === 'csv' ? 'Preparing…' : 'Download CSV (for portal upload)'}
        </button>
        <button
          onClick={() => download('pdf')}
          disabled={downloading !== null}
          style={{ padding: '10px 18px', background: '#fff', color: '#4b2a6b', border: '1px solid #4b2a6b', borderRadius: 8, fontSize: 13.5, fontWeight: 600, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 8 }}
        >
          <FileText size={14} /> {downloading === 'pdf' ? 'Preparing…' : 'Download PDF (archive copy)'}
        </button>
      </div>

      <div style={{ marginTop: 18, padding: 14, background: '#faf5ea', border: '1px solid #ece3d2', borderRadius: 10, fontSize: 13, color: '#46362a', lineHeight: 1.55 }}>
        <strong>How to file:</strong>
        <ol style={{ margin: '6px 0 0 18px', padding: 0 }}>
          <li>Download the CSV above.</li>
          <li>Log in to the U-DISE+ portal (<code>udiseplus.gov.in</code>) with your school's state/district credentials.</li>
          <li>Go to <em>DCF Entry</em> and upload the CSV, or copy values section-by-section into the online form.</li>
          <li>Fill any "— (enter manually)" rows — typically staff qualification bands and teacher employment categories.</li>
          <li>Submit. A receipt is generated by the portal.</li>
        </ol>
      </div>
    </div>
  );
};

export default GeneratorsPage;
